import { ModalHubEvent } from './interface'
import { RouterRegistry } from '../../util/routerRegistry'
import config from './config'
import { loadRemote } from '@module-federation/runtime'
import { useEmitter } from '../../pmd/composition/useEmitter'
import { useScopedStore } from '../setupMicroAppStore'

/*
Event Data Schema Definition: Event = modal-hub
LOAD:
{
    type: 'load',
    modalName: string,
}
UNLOAD:
{
    type: 'unload',
    modalName: string,
}
OPEN:
{
    type: 'open' | 'close' | 'mounted',
    modalName: string,
    data: {
        mode: 'create' | 'edit' | 'duplicate' | 'delete' | 'view',
        data: any,
        id: string,
        store: {
            state,
            dispatch,
            commit,
            getters
        }
    }
}
CLOSE:
{
    type: 'open' | 'close' | 'mounted',
    modalName: string,
    data: {
        result: 'created' | 'edited' | 'deleted' | 'closed',
        id: string,
    }
}
MOUNTED:
{
    type: 'open' | 'close' | 'mounted',
    modalName: string,
}
Event Data Schema Definition: Event = modal-hub-provide
OPEN:
{
    type: 'open' | 'close'
    modalName: string
}
CLOSE:
{
    type: 'open' | 'close' | 'mounted',
    modalName: string,
    data: {
        result: 'created' | 'edited' | 'deleted' | 'closed',
        id: string,
    }
}
LOADED:
{
    type: 'loaded',
    modalName: string,
}
UNLOADED:
{
    type: 'unloaded',
    modalName: string,
}
*/

class ModalHub {
  private config: any
  private emitter: any
  private modalApps: any = {}
  constructor() {
    this.config = config
    this.emitter = useEmitter()
  }

  public register() {
    this.subscribeToModalEvents()
  }

  private subscribeToModalEvents() {
    this.emitter?.on('modal-hub', (event: ModalHubEvent) => {
      if (event.type === 'open') {
        this.openModal(event.modalName, event.data)
      } else if (event.type === 'close') {
        this.closeModal(event.modalName, event.data)
      } else if (event.type === 'load') {
        this.preloadModal(event.modalName)
      } else if (event.type === 'unload') {
        this.unloadModal(event.modalName)
      }
    })
  }

  private async preloadModal(modalName: string) {
    const modalConfig = this.config[modalName]
    if (!modalConfig) {
      console.error(`Modal ${modalName} not found`)
      return
    }

    try {
      await this.loadModalBuilds(modalName, modalConfig, null)
      // Emit event that modal JS has been loaded
      this.emitter.emit('modal-hub-provide', {
        type: 'loaded',
        modalName: modalName,
      })
    } catch (error) {
      console.error(`Error preloading modal ${modalName}:`, error)
    }
  }

  private unloadModal(modalName: string) {
    if (!this.modalApps[modalName]) {
      console.warn(`Modal ${modalName} is not loaded`)
      return
    }

    try {
      // If modal component is mounted, unmount it first
      if (this.modalApps[modalName]?.modalComponent?.unmount) {
        this.modalApps[modalName].modalComponent.unmount()

        // Clean up the DOM container
        const containerId = 'modal-hub-' + modalName
        const modalContainer = document.getElementById(containerId)
        if (modalContainer) {
          document.body.removeChild(modalContainer)
        }
      }

      // Clear the cached modal JS
      delete this.modalApps[modalName]

      // Emit event that modal JS has been unloaded
      this.emitter.emit('modal-hub-provide', {
        type: 'unloaded',
        modalName: modalName,
      })
    } catch (error) {
      console.error(`Error unloading modal ${modalName}:`, error)
    }
  }

  private async loadModalBuilds(modalName: string, modalConfig: any, data: any) {
    if (this.modalApps[modalName]) {
      // Load from cache if already loaded
      return this.modalApps[modalName]
    }
    const [createApp, ModalApp] = await Promise.all([
      loadRemote(modalConfig.vue3).then((m: any) => m.createApp), // after the vue3 migration, this will be removed since we can directly use the createApp function from spm-ts vue3
      loadRemote(modalConfig.path).then((m: any) => m.default),
    ])
    this.modalApps[modalName] = { createApp, ModalApp }
    return this.modalApps[modalName]
  }

  private async createAndMountModalApp(modalName: string, { createApp, ModalApp }: any, data: any) {
    try {
      const defaultStoreObj: any = {
        state: ['user', 'locations', 'auth'], // default states to be used in all the modals
      }

      defaultStoreObj.dispatch = data.store?.dispatch || []
      defaultStoreObj.commit = data.store?.commit || []
      defaultStoreObj.getters = data.store?.getters || []
      defaultStoreObj.state = defaultStoreObj.state.concat(data.store?.state || [])
      const router = RouterRegistry.getRouter()
      const modalComponent = createApp(ModalApp, {
        mode: data.mode,
        route: router?.currentRoute,
        // eslint-disable-next-line vue/no-deprecated-data-object-declaration, vue/no-shared-component-data
        data: data.data,
        id: data.id,
        emitter: this.emitter,
        store: useScopedStore(defaultStoreObj),
      })

      // create a div with id modal-hub-container
      const containerId = 'modal-hub-' + modalName
      let modalContainer = document.getElementById(containerId)
      if (!modalContainer) {
        modalContainer = document.createElement('div')
        modalContainer.id = containerId
        document.body.appendChild(modalContainer)
      }

      // Mount the component
      modalComponent.mount(modalContainer)
      this.modalApps[modalName].modalComponent = modalComponent
      return true
    } catch (error) {
      console.error(`Error mounting modal ${modalName}:`, error)
      return false
    }
  }

  private async openModal(modalName: string, data: any) {
    let modalConfig = this.config[modalName]
    if (!modalConfig) {
      console.error(`Modal ${modalName} not found`)
      return
    }

    try {
      const modalAppData = await this.loadModalBuilds(modalName, modalConfig, data)
      await this.createAndMountModalApp(modalName, modalAppData, data)
    } catch (error) {
      console.error(`Error opening modal ${modalName}:`, error)
    }
  }

  private closeModal(modalName: string, data: any) {
    this.emitter.emit(`modal-hub-provide`, {
      modalName: modalName,
      data: {
        type: 'close',
        data: data,
      },
    })
    if (this.modalApps[modalName]?.modalComponent?.unmount) {
      try {
        this.modalApps[modalName].modalComponent.unmount()
        delete this.modalApps[modalName]?.modalComponent

        // Clean up the DOM container
        const containerId = 'modal-hub-' + modalName
        const modalContainer = document.getElementById(containerId)
        if (modalContainer) {
          document.body.removeChild(modalContainer)
        }
      } catch (error) {
        console.error(`Error closing modal ${modalName}:`, error)
      }
    }
  }
}

export default new ModalHub()
